"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.HivemindContext = void 0;
exports.HivemindProvider = HivemindProvider;
exports.useCohorts = useCohorts;
exports.useParameter = useParameter;
exports.withCohortData = withCohortData;
exports.withCohorts = withCohorts;
exports.withParameter = withParameter;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _addhoc = _interopRequireDefault(require("addhoc"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { (0, _defineProperty2.default)(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
/**
 * @typedef {Object} UseParameterOpts
 * @prop {string} parameter Name of the parameter
 * @prop {Object} defaultValue The value of the parameter to default to if the subject is ineligible or the parameter
 * is not yet configured
 * @prop {Object} [attributes] An object of attributes you may want to pass to send to configuration backends for
 * experiment eligibility
 */
/**
 * @typedef {Object} GetParameterValueResult
 * @prop {Object} parameterValue The value of the parameter
 */
/**
 * @typedef {Object} CohortsOpts
 * @prop {string[]} [experiments] - An array of experiments that you want to request. Required if labels not set.
 * @prop {string[]} [labels] - An array of labels for experiments that you want to request. Required if experiments not
 * set.
 * @prop {Object} [attributes] - An object of attributes you may want to send to configuration backends for experiment
 * eligibility.
 */
/**
 * @typedef {Object} HivemindClient
 * @prop {Promise<boolean>} ready - Whether the Hivemind client is ready
 * @prop {(options: UseParameterOpts) => GetParameterValueResult} getParameterValue
 * @prop {(options: CohortsOpts) => Object} getCohorts
 */
/** @type {HivemindClient} */
const nullClient = {
  get ready() {
    return Promise.reject(new Error('No client initialized. Did you use HivemindProvider?'));
  },
  getParameterValue: () => {
    throw new Error('Not implemented.');
  },
  getCohorts: () => {
    throw new Error('Not implemented.');
  }
};
const defaultContextValue = {
  client: nullClient,
  ready: false,
  /** @type {React.Dispatch<React.SetStateAction<boolean>>} */
  setReady: () => {}
};
/**
 * React context to store Hivemind client.
 *
 * @public
 */
const HivemindContext = _react.default.createContext(defaultContextValue);

/**
 * Provides access to HivemindContext, which contains a HivemindClient instance, its readiness state, and a function to
 * set that readiness state.
 *
 * @param {Object} props - Props that include the Hivemind client and children.
 * @param {React.ReactNode} [props.children] - Any children that should have access to the Hivemind
 * context.
 * @param {HivemindClient} props.client - A HivemindClient instance.
 * @param {boolean} [props.handleReady=true] - Whether HivemindProvider should handle waiting for the Hivemind client
 * to be ready.
 * @param {React.ReactElement} [props.LoadingComponent] - A component to render while waiting for the Hivemind client to
 * be ready. If not set, null will be rendered.
 * @returns {React.ReactElement} - React components that have access to HivemindContext.
 * @public
 */
exports.HivemindContext = HivemindContext;
function HivemindProvider(_ref) {
  let {
    children,
    client,
    handleReady = true,
    LoadingComponent
  } = _ref;
  const [ready, setReady] = (0, _react.useState)(false);
  (0, _react.useEffect)(() => {
    if (handleReady) {
      client.ready.then(() => {
        setReady(true);
      });
    }
  }, []);
  if (handleReady && !ready) {
    return LoadingComponent ? LoadingComponent : null;
  }
  return /*#__PURE__*/_react.default.createElement(HivemindContext.Provider, {
    value: {
      client,
      ready,
      setReady
    }
  }, children);
}

/**
 * Prop types for HivemindProvider.
 */
HivemindProvider.propTypes = {
  children: _propTypes.default.node,
  client: _propTypes.default.object.isRequired,
  handleReady: _propTypes.default.bool,
  LoadingComponent: _propTypes.default.node
};

/**
 * Uses the HivemindContext client to return a parameter value.
 *
 * @param {UseParameterOpts} options - Options object used to retrieve the parameter value.
 * @returns {Object} - The parameter value.
 * @public
 */
function useParameter(_ref2) {
  let {
    parameter,
    defaultValue,
    attributes
  } = _ref2;
  const {
    client
  } = (0, _react.useContext)(HivemindContext);
  return client.getParameterValue({
    parameter,
    defaultValue,
    attributes
  }).parameterValue;
}

/**
 * @typedef {(value: Object) => V} MapValueToPropsFn
 * @template V
 */

/**
 * Higher-order component that provides a parameter value as props to the wrapped component.
 *
 * @param {React.ComponentType<P>} WrappedComponent - The component to be wrapped to use a parameter value.
 * @param {Object} options - Options object used to retrieve the parameter value and configure it for use.
 * @param {string} options.parameter - Name of the parameter.
 * @param {Object} [options.attributes] - An object of attributes you may want to send to configuration backends for
 * experiment eligibility.
 * @param {MapValueToPropsFn<V>} [options.mapParameterValueToProps] - A function that maps the parameter value to
 * component props that should be passed to the wrapped component. If not set, the parameter value is passed directly
 * as the component props.
 * @returns {React.ComponentType<P & V>} A wrapped component that provides the parameter value as props.
 * @template P
 * @template V
 * @public
 */
function withParameter(WrappedComponent, _ref3) {
  let {
    parameter,
    attributes,
    mapParameterValueToProps
  } = _ref3;
  return (0, _addhoc.default)(getWrappedComponent => {
    const parameterValue = useParameter({
      parameter,
      defaultValue: {},
      attributes
    });
    const wrappedComponentProps = mapParameterValueToProps ? mapParameterValueToProps(parameterValue) : parameterValue;
    return getWrappedComponent(wrappedComponentProps);
  }, 'withParameter')(WrappedComponent);
}

/**
 * Uses the HivemindContext client to return cohort assignments.
 *
 * @param {CohortsOpts} options - Options object used to retrieve the cohort assignments.
 * @returns {Object} - The cohort assignments.
 * @public
 */
function useCohorts(_ref4) {
  let {
    experiments,
    labels,
    attributes
  } = _ref4;
  const {
    client
  } = (0, _react.useContext)(HivemindContext);
  return client.getCohorts({
    experiments,
    labels,
    attributes
  });
}

/**
 * Higher-order component that provides cohort assignments as the cohort prop to the wrapped component.
 *
 * @param {React.ComponentType<P>} WrappedComponent - The component to be wrapped to use cohort assignments.
 * @param {Object} options - Options object used to retrieve cohorts.
 * @param {string[]} [options.experiments] - An array of experiments that you want to request. Required if labels not
 * set.
 * @param {string[]} [options.labels] - An array of labels for experiments that you want to request. Required if
 * experiments not set.
 * @param {Object} [options.attributes] - An object of attributes you may want to send to configuration backends for
 * experiment eligibility.
 * @param {MapValueToPropsFn<V>} [options.mapCohortsToProps] - A function that maps the cohort assignments to component
 * props that should be passed to the wrapped component. If not set, the cohort assignments are passed as the cohorts
 * prop.
 * @returns {React.ComponentType<P & V>} A wrapped component that provides the cohort assignments as the cohort prop.
 * @template P
 * @template V
 * @public
 */
function withCohorts(WrappedComponent, _ref5) {
  let {
    experiments,
    labels,
    attributes,
    mapCohortsToProps
  } = _ref5;
  return (0, _addhoc.default)(getWrappedComponent => {
    const cohorts = useCohorts({
      experiments,
      labels,
      attributes
    });
    const wrappedComponentProps = mapCohortsToProps ? mapCohortsToProps(cohorts) : cohorts;
    return getWrappedComponent(wrappedComponentProps);
  }, 'withCohorts')(WrappedComponent);
}

/**
 * * Higher-order component that provides cohort data from an experiment as props to the wrapped component.
 *
 * @param {React.ComponentType<P>} WrappedComponent - The component to be wrapped to use cohort data for an experiment.
 * @param {Object} options - Options object used to retrieve the cohort data and configure it for use.
 * @param {string} options.experiment - Name of the experiment.
 * @param {Object} [options.attributes] - An object of attributes you may want to send to configuration backends for
 * experiment eligibility.
 * @param {MapValueToPropsFn<V>} [options.mapCohortDataToProps] - A function that maps the cohort data object to
 * component props that should be passed to the wrapped component. If not set, the cohort data will be directly used.
 * @returns {React.ComponentType<P & V>} A wrapped component that provides the cohort data as props.
 * @template P
 * @template V
 * @public
 */
function withCohortData(WrappedComponent, _ref6) {
  let {
    experiment,
    attributes,
    mapCohortDataToProps
  } = _ref6;
  return withCohorts(WrappedComponent, {
    experiments: [experiment],
    attributes,
    mapCohortsToProps: cohortAssignment => {
      const {
        data,
        cohortId
      } = cohortAssignment.cohorts[experiment];
      const cohortData = _objectSpread({}, data, {
        cohortId
      });
      return mapCohortDataToProps ? mapCohortDataToProps(cohortData) : cohortData;
    }
  });
}